import { useState, useCallback, useMemo } from 'react';
import { StorageManager } from '../utils/storageManager';
import { WikimediaImage } from '../types/types';

interface GameStateToSave {
  currentRound: number;
  hasGuessed: boolean;
  yearInput: string[];
  revealedDigits: boolean[];
  challengeRevealedDigits: boolean[];
  revealUsedThisRound: boolean;
  score?: number;
  guessHistory?: any[];
  allGuesses: any[];
  pointsPerRound: number[];
  pointsGained: number | null;
  viewingResults?: boolean;
  challengeResultData?: any;
  skipsRemaining?: number;
  currentImageData?: WikimediaImage | null;
}

export const useProgressPersistence = (selectedDate?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const todayDateForHook = StorageManager.getTodayDate();

  const saveGameState = useCallback(
    (data: GameStateToSave): boolean => {
      try {
        const currentDate = selectedDate || todayDateForHook;
        const storageKey = StorageManager.KEYS.ACTIVE_GAME + currentDate;

        const stateToSave = {
          ...data,
          hasGuessed: data.hasGuessed === true,
          revealedDigits: data.revealedDigits.map((v) => v === true),
          challengeRevealedDigits: data.challengeRevealedDigits.map(
            (v) => v === true
          ),
          revealUsedThisRound: data.revealUsedThisRound === true,
        };

        const jsonString = JSON.stringify(stateToSave);

        localStorage.setItem(storageKey, jsonString);

        // Verify the save immediately
        const verifyData = localStorage.getItem(storageKey);
        if (!verifyData) {
          return false;
        }

        try {
          const parsedVerify = JSON.parse(verifyData);
          if (parsedVerify.currentRound !== data.currentRound) {
            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      } catch (e) {
        return false;
      }
    },
    [selectedDate, todayDateForHook]
  );

  const loadGameState = useCallback((): GameStateToSave | null => {
    setIsLoading(true);
    try {
      const currentDate = selectedDate || todayDateForHook;
      const gameState = StorageManager.loadActiveGame(currentDate);
      return gameState as GameStateToSave | null;
    } catch (e) {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, todayDateForHook]);

  const clearGameState = useCallback(() => {
    try {
      const currentDate = selectedDate || todayDateForHook;
      const storageKey = StorageManager.KEYS.ACTIVE_GAME + currentDate;
      localStorage.removeItem(storageKey);
    } catch (e) {}
  }, [selectedDate, todayDateForHook]);

  return useMemo(
    () => ({
      isLoading,
      saveGameState,
      loadGameState,
      clearGameState,
    }),
    [isLoading, saveGameState, loadGameState, clearGameState]
  );
};

export default useProgressPersistence;
